import React from "react"
import PropTypes from "prop-types"

import { getPageMenuItemsTrail } from "./layouts/helpers/MenuHelpers"
import PathTransformer from "./parsers/PathTransformer"

import { Link, Trans } from "gatsby-plugin-react-i18next"

import * as cascadingMenuStyles from "../styles/CascadingMenu.module.css"

const flatListToHierarchical = (
  data = [],
  { idKey = "id", parentKey = "parentId", childrenKey = "children" } = {},
) => {
  const tree = []
  const childrenOf = {}
  data.forEach(item => {
    const newItem = { ...item }
    const { [idKey]: id, [parentKey]: parentId = 0 } = newItem
    childrenOf[id] = childrenOf[id] || []
    newItem[childrenKey] = childrenOf[id]
    parentId
      ? (childrenOf[parentId] = childrenOf[parentId] || []).push(newItem)
      : tree.push(newItem)
  })
  return tree
}

const PageMenuReferencedId = menuItem => {
  if (!menuItem.connectedNode || !menuItem.connectedNode.node) return null
  return menuItem.connectedNode.node.id || null
}

const activeStyle = (item, activePageId) => {
  return activePageId === PageMenuReferencedId(item)
    ? cascadingMenuStyles.menuItemActive
    : ``
}

const endPath = (item, pageContext) => {
  return PathTransformer(
    item.path,
    pageContext.language,
    pageContext.i18n.defaultLanguage,
    pageContext.i18n.languages,
  )
}

const subMenuCustomEntries = item => {
  let entries = []

  const subMenuEntries =
    (item.connectedNode &&
      item.connectedNode.node &&
      item.connectedNode.node.subMenuEntries) ||
    []

  const menuPages = Object.keys(subMenuEntries).sort()

  entries = menuPages
    .filter(
      p =>
        subMenuEntries[p].subMenuPageName && subMenuEntries[p].subMenuPageLink
    )
    .map(p => ({
      name: subMenuEntries[p].subMenuPageName,
      path:
        subMenuEntries[p].subMenuPageLink &&
        subMenuEntries[p].subMenuPageLink.uri,
    }))

  return entries
}

const subMenuCustomExternalEntries = item => {
  let entries = []

  const subMenuEntries =
    (item.connectedNode &&
      item.connectedNode.node &&
      item.connectedNode.node.subMenuExternalEntries) ||
    []

  const menuPages = Object.keys(subMenuEntries).sort()

  entries = menuPages
    .filter(
      p => subMenuEntries[p].subMenuLinkName && subMenuEntries[p].subMenuLinkUrl
    )
    .map(p => ({
      name: subMenuEntries[p].subMenuLinkName,
      path: subMenuEntries[p].subMenuLinkUrl,
    }))

  return entries
}

const CascadingMenu = props => {
  const { menuItems, pageContext, activePageId } = props
  const hierarchicalMenuItems = flatListToHierarchical(menuItems)

  const openMenuItems = getPageMenuItemsTrail(activePageId, menuItems).map(
    it => [it.id, true],
  )

  const [openMenuLinks, setOpenMenuLinks] = React.useState(
    Object.fromEntries(openMenuItems),
  )

  const displayThirdLevel = items => {
    return items.map((thirdlevelitem, index) => (
      <div
        key={index}
        className={`row ${cascadingMenuStyles.thirdLevelMenuItemHolder}`}
      >
        <div className="col-12">
          <Link
            className={`${activeStyle(thirdlevelitem, activePageId)}`}
            to={endPath(thirdlevelitem, pageContext)}
            onClick={() => props.onToggle(props.menuId)}
          >
            <Trans>{thirdlevelitem.label}</Trans>
          </Link>
          {subMenuCustomEntries(thirdlevelitem).map((entry, eindx) => (
            <Link
              key={eindx}
              className={`${cascadingMenuStyles.submenuLink}`}
              to={endPath(entry, pageContext)}
            >
              {entry.name}
            </Link>
          ))}
          {subMenuCustomExternalEntries(thirdlevelitem).map((entry, eindx) => (
            <a
              key={eindx}
              href={entry.path}
              target="_blank"
              rel="noreferrer"
              className={`${cascadingMenuStyles.submenuLink}`}
            >
              {entry.name}
            </a>
          ))}
        </div>
      </div>
    ))
  }

  const displayActions = (item, expandColapseStyle, horizontal = true) => {
    return (
      item.children.length !== 0 && (
        <a
          href="#!"
          className={expandColapseStyle}
          onClick={evt => {
            evt.preventDefault()
            setOpenMenuLinks(prevState => ({
              ...prevState,
              [item.id]: !!!prevState[item.id],
            }))
          }}
        >
          {openMenuLinks[item.id] && horizontal && (
            <i className="bi bi-chevron-left"></i>
          )}
          {!openMenuLinks[item.id] && horizontal && (
            <i className="bi bi-chevron-right"></i>
          )}

          {openMenuLinks[item.id] && !horizontal && (
            <i className="bi bi-chevron-up"></i>
          )}
          {!openMenuLinks[item.id] && !horizontal && (
            <i className="bi bi-chevron-down"></i>
          )}
        </a>
      )
    )
  }

  return (
    <div className={cascadingMenuStyles.wrapper}>
      {hierarchicalMenuItems.map((item, idx) => (
        <div
          key={idx}
          className={`row ${cascadingMenuStyles.menuItemHolder}  ${
            openMenuLinks[item.id]
              ? cascadingMenuStyles.menuItemHolderExpanded
              : cascadingMenuStyles.menuItemHolderClosed
          }`}
        >
          <div className="col-11">
            <Link
              className={`${activeStyle(item, activePageId)}`}
              to={endPath(item, pageContext)}
              onClick={() => props.onToggle(props.menuId)}
            >
              <Trans>{item.label}</Trans>
            </Link>

            {item.children.map((subitem, indx) => (
              <div
                key={indx}
                className={`row ${cascadingMenuStyles.subMenuItemHolder} ${
                  openMenuLinks[subitem.id]
                    ? cascadingMenuStyles.subMenuItemHolderExpanded
                    : cascadingMenuStyles.subMenuItemHolderClosed
                }`}
              >
                <div className="col-12">
                  <div
                    className={`row ${cascadingMenuStyles.subMenuItemWrapper}`}
                  >
                    <div
                      className={`col-12 ${cascadingMenuStyles.subMenuItem}`}
                    >
                      {subitem.children && subitem.children.length > 0 && (
                        <span
                          className={`${
                            cascadingMenuStyles.linkItem
                          } ${activeStyle(subitem, activePageId)}`}
                        >
                          <Trans>{subitem.label}</Trans>
                        </span>
                      )}
                      {(!subitem.children || !subitem.children.length) && (
                        <Link
                          className={`${
                            cascadingMenuStyles.linkItem
                          } ${activeStyle(subitem, activePageId)}`}
                          to={endPath(subitem, pageContext)}
                          onClick={() => props.onToggle(props.menuId)}
                        >
                          <Trans>{subitem.label}</Trans>
                        </Link>
                      )}
                      {displayActions(
                        subitem,
                        cascadingMenuStyles.subMenuItemExpandColapse,
                      )}
                    </div>
                  </div>

                  {displayThirdLevel(subitem.children)}
                </div>
              </div>
            ))}
          </div>
          <div className={`col-1 g-0 ${cascadingMenuStyles.menuItemActions}`}>
            {displayActions(
              item,
              cascadingMenuStyles.menuItemExpandColapse,
              false,
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default CascadingMenu

CascadingMenu.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      parentId: PropTypes.string,
      label: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      connectedNode: PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired,
          excerpt: PropTypes.string,
        }),
      }),
    }).isRequired,
  ).isRequired,
  activePageId: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
}
